import Swiper, { Navigation, Scrollbar, Mousewheel, FreeMode } from 'swiper';

const classes = {
  wrapperCenter: 'swiper-wrapper--center',
};

const productExplorer = (data) => ({
  numSlides: 0,
  selectedIdx: 0,
  type: null,
  profileTag: null,
  carouselData: [],

  /**
   * Centers the wrapper swiper fully fits in container.
   */
  swiperCenterOnNoScroll: (swiper) => {
    const isSwipeable = swiper.virtualSize > swiper.size;

    if (!isSwipeable) {
      swiper.wrapperEl?.classList.add(classes.wrapperCenter);
    } else {
      swiper.wrapperEl?.classList.remove(classes.wrapperCenter);
    }
  },

  get swiperTabsConfig() {
    return {
      slidesPerView: 'auto',
      modules: [Mousewheel, FreeMode],
      freeMode: {
        momentum: false,
      },
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        ppClick: (_, event) => {
          event.target.dispatchEvent(new CustomEvent('swiper-tap'));
        },
        init: this.swiperCenterOnNoScroll,
        resize: this.swiperCenterOnNoScroll,
      },
    };
  },

  get swiperConfig() {
    return {
      slidesPerView: 'auto',
      modules: [Navigation, Scrollbar, Mousewheel],
      scrollbar: {
        el: '.swiper-scrollbar',
        hide: true,
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
        disabledClass: 'swiper-button-hidden',
      },
      mousewheel: {
        forceToAxis: true,
      },
      grabCursor: true,
      on: {
        init: this.swiperCenterOnNoScroll,
        resize: this.swiperCenterOnNoScroll,
      },
    };
  },

  get selectedProducts() {
    const foundCarousel = this.carouselData.find(
      (entry) => entry.handle === this.selectedCollectionHandle
    );
    const firstNumProducts =
      foundCarousel?.results?.slice(0, this.numSlides) || {};

    return firstNumProducts;
  },

  get selectedCollectionHandle() {
    return this.carouselData[this.selectedIdx]?.handle;
  },

  get selectedCollectionTitle() {
    return this.carouselData[this.selectedIdx]?.title;
  },

  get selectedCollectionUrl() {
    return this.carouselData[this.selectedIdx]?.url;
  },

  get profileTagNormalized() {
    return this.profileTag || 'similar';
  },

  get searchspringType() {
    const { type } = this;
    if (type === 'collection') return 'search';
    if (type === 'recommendations') return 'recommendations';
    return '';
  },

  get searchspringRecommendationsTag() {
    return this.profileTagNormalized;
  },

  cacheNodes() {
    this.swiperTabsEl = this.$el.querySelector('.swiper[swiper-tab-bank]');
    this.swiperContentEls = this.$el.querySelectorAll(
      '.swiper:not([swiper-tab-bank])'
    );
  },

  async getSearchspringRecommendations() {
    return [];
    // const results = await ssFetchRecommendations(this.profileTagNormalized);
    // if (results.length > this.numSlides) {
    //   return results.slice(0, this.numSlides);
    // }
    // return results;
  },

  async getSearchspringCollection(handle) {
    return new Promise((_, reject) => {
      reject(new Error('Searchspring is not supported anymore.'));
    })

  },

  /**
   * Fetch carousel data from Searchspring API.
   * - Currently only supports fetching either collections data or recommendations, not both
   * - This could be extended to support some tabs being collections and others being
   *   product recommendations.
   */
  async getCarouselData() {
    const isCollection = this.type === 'collection';
    const isRecommendations = this.type === 'recommendations';
    let carouselData = [];

    /**
     * Map the swiper element to an object containing data about the carousel.
     */
    const mapSSData = (swiper) => {
      let data = {
        results: [],
      };

      if (isCollection) {
        data = {
          ...data,
          handle: swiper.dataset.collectionHandle,
          title: swiper.dataset.collectionTitle,
          url: swiper.dataset.collectionUrl,
        };
      }

      return data;
    };

    /**
     * Map the carouselData to a promise that will fetch the data from the Searchspring API.
     */
    const mapSSRequests = (entry) => {
      let request;

      if (isCollection) {
        request = this.getSearchspringCollection(entry.handle);
      }

      if (isRecommendations) {
        request = this.getSearchspringRecommendations();
      }

      // If no request, return a dummy promise to retain the indices from carouselData.
      return request || Promise.resolve();
    };

    /**
     * Merge Searchspring API responses into the carouselData objects.
     */
    const mergeSSData = (response, idx) => {
      if (isCollection) {
        carouselData[idx].results = response?.results;
      }

      if (isRecommendations) {
        /**
         * Searchspring Recommendations API returns results in a different format
         * to the Search API. We need to add a `uid` property so the `product-card`
         * Alpine component can function correctly.
         */
        carouselData[idx].results = response.map((product) => ({
          ...product,
          uid: product.id,
        }));
      }
    };

    carouselData = Array.from(this.swiperContentEls).map(mapSSData);
    const ssRequests = carouselData.map(mapSSRequests);
    await Promise.all(ssRequests).then((responses) =>
      responses.forEach(mergeSSData)
    );
    return carouselData;
  },

  async setData() {
    if (data) {
      this.numSlides = data.numSlides;
      this.type = data.type;
      this.profileTag = data.profileTag;
    }

    const carouselData = await this.getCarouselData();
    this.carouselData = carouselData?.filter((entry) => entry.results?.length);
  },

  buildSwipers() {
    // Tabs setup
    if (this.swiperTabsEl) {
      this.swiperTabsInstance = new Swiper(
        this.swiperTabsEl,
        this.swiperTabsConfig
      );
    }

    // Content setup
    if (this.swiperContentEls) {
      this.swiperContentInstances = Array.from(this.swiperContentEls).map(
        (swiperEl) => new Swiper(swiperEl, this.swiperConfig)
      );
    }
  },

  async init() {
    this.cacheNodes();
    await this.setData();
    this.buildSwipers();
  },
});

export default productExplorer;
